import { useEffect, useRef, useState } from 'react';
import service from '../../../../services/billing.service';
import { CHECK_EMAIL_DEBOUNCE_MS, EMAIL_MAX_LENGTH, EMAIL_REGEX } from '../checkout.constants';
import { readErrorStatus } from '../checkout.helpers';
import type { EmailCheckState } from '../checkout.types';

/**
 * Debounced availability check for the checkout email. Validates format
 * client-side first, then calls `service.checkEmail`. A monotonic run id
 * discards stale responses so only the latest keystroke's result wins.
 */
export function useEmailCheck(email: string): EmailCheckState {
  const [emailCheckState, setEmailCheckState] = useState<EmailCheckState>({ kind: 'idle' });
  const checkRunIdRef = useRef(0);

  useEffect(() => {
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      checkRunIdRef.current++;
      setEmailCheckState({ kind: 'idle' });
      return;
    }
    if (!EMAIL_REGEX.test(trimmed) || trimmed.length > EMAIL_MAX_LENGTH) {
      checkRunIdRef.current++;
      setEmailCheckState({ kind: 'invalid-format' });
      return;
    }

    setEmailCheckState({ kind: 'checking' });
    const runId = ++checkRunIdRef.current;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { status } = await service.checkEmail({ email: trimmed.toLowerCase() });
        if (cancelled || runId !== checkRunIdRef.current) return;
        setEmailCheckState(
          status === 'has-active-sub' ? { kind: 'has-active-sub' } : { kind: 'available' }
        );
      } catch (err) {
        if (cancelled || runId !== checkRunIdRef.current) return;
        if (readErrorStatus(err) === 400) {
          setEmailCheckState({ kind: 'invalid-format' });
        } else {
          setEmailCheckState({ kind: 'verify-failed' });
        }
      }
    }, CHECK_EMAIL_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [email]);

  return emailCheckState;
}
